import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';
import ActiveSchoolSwitcher from './ActiveSchoolSwitcher';
import ProfileSwitcher from './ProfileSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { getEffectiveNavRole, canFindSchoolAsCoach, isFreelanceCoach } from '../utils/roles';
import { useSchoolCashDeskEnabled } from '../hooks/useSchoolCashDeskEnabled';
import Button from './ui/Button';
import Logo from './Logo';
import NotificationBell from './NotificationBell';

/** @typedef {{ href: string, icon: string, labelKey: string, roles?: string[] | null, requiresCashDesk?: boolean }} NavItem */

/**
 * Menu labels resolve via i18n namespace `nav` (labelKey).
 */
const NAV_MENU = [
    { type: 'link', href: '/', icon: '🏠', labelKey: 'home', roles: null },
    { type: 'link', href: '/reports', icon: '📊', labelKey: 'reports', roles: ['Admin'] },
    { type: 'link', href: '/schedule', icon: '🗓️', labelKey: 'schedule', roles: null },

    {
        type: 'group',
        id: 'training',
        icon: '📚',
        labelKey: 'groups.training',
        roles: ['Owner', 'Coach', 'Admin'],
        items: [
            { href: '/students', icon: '👥', labelKey: 'students', roles: ['Owner', 'Coach', 'Admin'] },
            { href: '/attendance', icon: '📅', labelKey: 'attendance', roles: ['Owner', 'Coach', 'Admin'] },
            { href: '/applications', icon: '📋', labelKey: 'applications', roles: ['Owner', 'Admin'] },
            { href: '/events', icon: '🏁', labelKey: 'events', roles: ['Owner', 'Admin'] },
        ],
    },

    {
        type: 'group',
        id: 'finance',
        icon: '💰',
        labelKey: 'groups.finance',
        roles: ['Owner', 'Admin'],
        items: [
            { href: '/school#tariffs', icon: '🎫', labelKey: 'tariffs', roles: ['Owner', 'Admin'] },
            { href: '/subscriptions', icon: '💳', labelKey: 'subscriptions', roles: ['Owner', 'Admin'] },
            { href: '/reports', icon: '📊', labelKey: 'reports', roles: ['Owner', 'Admin'] },
            { href: '/cashier', icon: '🧾', labelKey: 'cashier', roles: ['Owner', 'Admin', 'Coach'], requiresCashDesk: true },
            { href: '/nomenclature', icon: '📦', labelKey: 'nomenclature', roles: ['Owner', 'Admin'], requiresCashDesk: true },
            { href: '/loyalty', icon: '🎁', labelKey: 'loyalty', roles: ['Owner', 'Admin'], requiresCashDesk: true },
        ],
    },

    {
        type: 'group',
        id: 'school',
        icon: '🏫',
        labelKey: 'groups.school',
        roles: ['Owner', 'Admin', 'Coach'],
        items: [
            { href: '/school', icon: '🏫', labelKey: 'schoolOverview', roles: ['Owner', 'Admin', 'Coach'] },
            { href: '/staff', icon: '🧑‍💼', labelKey: 'staff', roles: ['Owner', 'Admin'] },
            { href: '/school/venues', icon: '🏟️', labelKey: 'venues', roles: ['Owner', 'Admin'] },
            { href: '/school/inventory', icon: '🛼', labelKey: 'inventoryRental', roles: ['Owner'] },
            { href: '/school/settings', icon: '⚙️', labelKey: 'settingsIntegrations', roles: ['Owner'] },
            { href: '/billing', icon: '💎', labelKey: 'platformBilling', roles: ['Owner'] },
        ],
    },

    { type: 'link', href: '/upload', icon: '🎬', labelKey: 'video', roles: null },
    { type: 'link', href: '/marketplace', icon: '🏆', labelKey: 'marketplace', roles: null },

    { type: 'link', href: '/coaches/find-school', icon: '🔍', labelKey: 'findSchool', roles: ['CoachFind'] },
    { type: 'link', href: '/my-balance', icon: '💳', labelKey: 'myBalance', roles: ['Member'] },
    { type: 'link', href: '/rental', icon: '🛼', labelKey: 'rental', roles: ['Member'] },
    { type: 'link', href: '/apply', icon: '📝', labelKey: 'apply', roles: ['Member', 'Parent'] },

    {
        type: 'group',
        id: 'platform',
        icon: '🛡️',
        labelKey: 'groups.platform',
        roles: ['Admin'],
        items: [
            { href: '/admin/platform-settings', icon: '⚙️', labelKey: 'adminPlatformSettings', roles: ['Admin'] },
            { href: '/admin/config', icon: '⚙️', labelKey: 'adminRemoteConfig', roles: ['Admin'] },
            { href: '/admin/school-billing', icon: '💎', labelKey: 'adminSchoolBilling', roles: ['Admin'] },
            { href: '/admin/integrations', icon: '🔑', labelKey: 'adminApiKeys', roles: ['Admin'] },
            { href: '/admin/email-settings', icon: '📧', labelKey: 'adminEmailSettings', roles: ['Admin'] },
            { href: '/admin/notifications/queue', icon: '📬', labelKey: 'adminNotificationQueue', roles: ['Admin'], badgeKey: 'failedNotifications' },
            { href: '/admin/activity-log', icon: '📋', labelKey: 'adminActivityLog', roles: ['Admin'] },
            { href: '/admin/sms-billing', icon: '📱', labelKey: 'adminSmsBilling', roles: ['Admin'] },
            { href: '/admin/sms', icon: '💬', labelKey: 'adminSmsMgmt', roles: ['Admin'] },
            { href: '/admin/person-merge', icon: '🔗', labelKey: 'adminDuplicates', roles: ['Admin'] },
            { href: '/admin/references', icon: '📚', labelKey: 'adminReferences', roles: ['Admin'] },
            { href: '/admin/i18n', icon: '🌐', labelKey: 'adminI18n', roles: ['Admin'] },
            { href: '/admin/pos-catalog', icon: '📋', labelKey: 'adminPosCatalog', roles: ['Admin'] },
            { href: '/admin/bugs', icon: '🐛', labelKey: 'adminBugs', roles: ['Admin'], badgeKey: 'newBugs' },
            { href: '/admin/testers', icon: '👥', labelKey: 'adminTesters', roles: ['Admin'] },
            { href: '/admin/marketplace/products', icon: '🏆', labelKey: 'adminMarketplace', roles: ['Admin'], badgeKey: 'pendingVerification' },
        ],
    },

    { type: 'link', href: '/profile', icon: '👤', labelKey: 'profile', roles: null },
];

/** Platform Admin: hide school-scoped navigation (no owned school context). */
const ADMIN_HIDDEN_HREFS = new Set([
    '/schedule', '/upload', '/marketplace',
    '/students', '/attendance', '/applications', '/events',
    '/school', '/staff', '/school/venues', '/school/inventory',
    '/school/settings',
    '/school#tariffs', '/subscriptions', '/cashier',
    '/nomenclature', '/loyalty', '/billing',
    '/my-balance', '/rental', '/apply', '/coaches/find-school',
]);

const ADMIN_HIDDEN_GROUPS = new Set(['training', 'school', 'finance']);

function isAdminHiddenHref(href) {
    if (!href) return false;
    if (ADMIN_HIDDEN_HREFS.has(href)) return true;
    const base = href.split('#')[0];
    return ADMIN_HIDDEN_HREFS.has(base);
}

function isAdminHiddenGroup(groupId) {
    return ADMIN_HIDDEN_GROUPS.has(groupId);
}

const NAV_OPEN_EVENT = 'onboarding-open-nav';
const NAV_EXPAND_GROUP_EVENT = 'onboarding-expand-nav-group';

const ONBOARDING_ATTR = {
    '/students': 'nav-students',
    '/schedule': 'nav-schedule',
    '/applications': 'nav-applications',
    '/staff': 'nav-staff',
    '/school/settings': 'nav-settings',
    '/subscriptions': 'nav-finance',
    '/school/inventory': 'nav-inventory',
    '/school/venues': 'nav-venues',
    '/cashier': 'nav-cashier',
    '/profile': 'nav-profile',
    '/attendance': 'nav-attendance',
    '/upload': 'nav-upload',
    '/my-balance': 'nav-balance',
    '/apply': 'nav-apply',
    '/rental': 'nav-rental',
};

const ROLE_ICONS = {
    Owner: '👑',
    Coach: '🎽',
    Member: '⚽',
    Staff: '🧑‍💼',
    Admin: '🛡️',
};

function roleBadgeLabel(role, t) {
    if (role === 'Coach' && isFreelanceCoach()) return `👤 ${t('roles.freelanceCoach')}`;
    const key = { Owner: 'owner', Coach: 'coach', Member: 'member', Staff: 'staff', Admin: 'admin' }[role];
    const icon = ROLE_ICONS[role] || '';
    return key ? `${icon} ${t(`roles.${key}`)}` : role;
}

function pathMatchesHref(href, pathname) {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(`${href}/`);
}

/** Самый конкретный href из списка, подходящий под текущий путь */
function resolveActiveHref(items, pathname) {
    let best = null;
    for (const item of items) {
        if (!pathMatchesHref(item.href, pathname)) continue;
        if (!best || item.href.length > best.length) best = item.href;
    }
    return best;
}

function canSeeItem(item, role, cashDeskEnabled) {
    if (role === 'Admin' && isAdminHiddenHref(item.href)) return false;
    if (item.requiresCashDesk && !cashDeskEnabled) return false;
    if (item.roles == null) return true;
    if (item.roles.includes('CoachFind')) return canFindSchoolAsCoach();
    return item.roles.includes(role);
}

function canSeeGroup(group, role, cashDeskEnabled) {
    if (role === 'Admin' && isAdminHiddenGroup(group.id)) return false;
    if (group.roles != null && !group.roles.includes(role)) return false;
    return group.items.some((item) => canSeeItem(item, role, cashDeskEnabled));
}

function filterMenu(menu, role, cashDeskEnabled) {
    return menu
        .map((entry) => {
            if (entry.type === 'link') {
                if (role === 'Admin' && isAdminHiddenHref(entry.href)) return null;
                return canSeeItem(entry, role, cashDeskEnabled) ? entry : null;
            }
            if (role === 'Admin' && isAdminHiddenGroup(entry.id)) return null;
            if (!canSeeGroup(entry, role, cashDeskEnabled)) return null;
            const items = entry.items.filter((item) => canSeeItem(item, role, cashDeskEnabled));
            return items.length ? { ...entry, items } : null;
        })
        .filter(Boolean);
}

function findActiveInMenu(menu, pathname, t) {
    if (pathname === '/profile' || pathname.startsWith('/profile/')) {
        return { labelKey: 'profile', groupId: null, label: t('profile') };
    }
    for (const entry of menu) {
        if (entry.type === 'link' && pathMatchesHref(entry.href, pathname)) {
            const topLinks = menu.filter((e) => e.type === 'link').map((e) => ({ href: e.href }));
            if (resolveActiveHref(topLinks, pathname) === entry.href) {
                return { labelKey: entry.labelKey, groupId: null, label: t(entry.labelKey) };
            }
        }
        if (entry.type === 'group') {
            const activeHref = resolveActiveHref(entry.items, pathname);
            const hit = entry.items.find((item) => item.href === activeHref);
            if (hit) return { labelKey: hit.labelKey, groupId: entry.id, label: t(hit.labelKey) };
        }
    }
    return { label: '', labelKey: '', groupId: null };
}

function NavLink({ item, pathname, activeHref, onNavigate, t, badges }) {
    const active = item.href === activeHref;
    const badge = item.badgeKey ? badges?.[item.badgeKey] : 0;
    return (
        <a
            href={item.href}
            data-onboarding={ONBOARDING_ATTR[item.href] || undefined}
            className={`sp-sidebar__link sp-sidebar__link--child${active ? ' sp-sidebar__link--active' : ''}`}
            onClick={onNavigate}
        >
            <span className="sp-sidebar__link-icon">{item.icon}</span>
            <span style={{ flex: 1 }}>{t(item.labelKey)}</span>
            {badge > 0 && (
                <span className="sp-badge sp-badge--danger" style={{ marginLeft: 6, fontSize: 11 }}>
                    {badge}
                </span>
            )}
        </a>
    );
}

function NavGroup({ group, pathname, expanded, onToggle, onNavigate, t, badges }) {
    const activeHref = resolveActiveHref(group.items, pathname);
    const hasActiveChild = Boolean(activeHref);

    return (
        <div className={`sp-sidebar__group${expanded ? ' sp-sidebar__group--open' : ''}${hasActiveChild ? ' sp-sidebar__group--has-active' : ''}`}>
            <button
                type="button"
                className="sp-sidebar__group-toggle"
                onClick={onToggle}
                aria-expanded={expanded}
            >
                <span className="sp-sidebar__link-icon">{group.icon}</span>
                <span className="sp-sidebar__group-label">{t(group.labelKey)}</span>
                <span className="sp-sidebar__group-chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
            </button>
            <div className="sp-sidebar__group-items">
                {group.items.map((item) => (
                    <NavLink
                        key={item.href}
                        item={item}
                        pathname={pathname}
                        activeHref={activeHref}
                        onNavigate={onNavigate}
                        t={t}
                        badges={badges}
                    />
                ))}
            </div>
        </div>
    );
}

export default function NavBar() {
    const { t, i18n } = useTranslation('nav');
    const username = localStorage.getItem('username') || '';
    const role = getEffectiveNavRole();
    const { enabled: cashDeskEnabled } = useSchoolCashDeskEnabled();
    const sportIcon = localStorage.getItem('sportIcon') || '🏆';
    const current = window.location.pathname;
    const [sideOpen, setSideOpen] = useState(false);
    const [badges, setBadges] = useState({});

    useEffect(() => {
        if (role !== 'Admin' || !localStorage.getItem('token')) return undefined;
        let cancelled = false;
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const req = { headers, timeout: 8000 };
        Promise.all([
            axios.get(`${getApiBaseUrl()}/admin/bugs/counts`, req),
            axios.get(`${getApiBaseUrl()}/admin/notifications/queue/stats`, req),
            axios.get(`${getApiBaseUrl()}/admin/marketplace/verification/pending-count`, req),
        ]).then(([bugsRes, queueRes, verRes]) => {
            if (!cancelled) {
                setBadges({
                    newBugs: bugsRes.data?.new ?? 0,
                    failedNotifications: queueRes.data?.failedCount ?? 0,
                    pendingVerification: verRes.data?.count ?? 0,
                });
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [role, current]);

    const menu = useMemo(
        () => filterMenu(NAV_MENU, role, cashDeskEnabled),
        [role, cashDeskEnabled]
    );

    const { label: currentLabel, groupId: activeGroupId } = useMemo(
        () => findActiveInMenu(menu, current, t),
        [menu, current, t, i18n.language]
    );

    const [expandedGroupId, setExpandedGroupId] = useState(activeGroupId);

    useEffect(() => {
        if (activeGroupId) setExpandedGroupId(activeGroupId);
    }, [activeGroupId]);

    const closeSidebar = useCallback(() => setSideOpen(false), []);

    useEffect(() => {
        document.body.classList.add('app-with-navbar');
        const openForTour = () => setSideOpen(true);
        const expandForTour = (e) => {
            const groupId = e.detail?.groupId;
            if (groupId) {
                setExpandedGroupId(groupId);
                setSideOpen(true);
            }
        };
        window.addEventListener(NAV_OPEN_EVENT, openForTour);
        window.addEventListener(NAV_EXPAND_GROUP_EVENT, expandForTour);
        return () => {
            document.body.classList.remove('app-with-navbar');
            window.removeEventListener(NAV_OPEN_EVENT, openForTour);
            window.removeEventListener(NAV_EXPAND_GROUP_EVENT, expandForTour);
        };
    }, []);

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 1024px)');
        const sync = () => { if (mq.matches) setSideOpen(false); };
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    function logout() {
        localStorage.clear();
        window.location.href = '/login';
    }

    function toggleGroup(id) {
        setExpandedGroupId((prev) => (prev === id ? null : id));
    }

    const avatarLetter = username.charAt(0).toUpperCase();

    return (
        <>
            <header className="sp-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <button
                        type="button"
                        className="sp-header__burger"
                        onClick={() => setSideOpen(true)}
                        aria-label={t('openMenu')}
                    >
                        ☰
                    </button>
                    <a href="/" className="sp-header__brand">
                        <span style={{ fontSize: 22, flexShrink: 0 }}>{sportIcon}</span>
                        <Logo variant="dark" height={34} />
                    </a>
                </div>

                <div className="navbar-center-label">
                    {currentLabel}
                </div>

                <div className="sp-header__actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {localStorage.getItem('token') ? <NotificationBell /> : null}
                    <ProfileSwitcher compact />
                    <ActiveSchoolSwitcher compact />
                    <span className="hide-mobile-decor sp-badge sp-badge--primary" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff' }}>
                        {roleBadgeLabel(role, t)}
                    </span>
                    <a
                        href="/profile"
                        data-onboarding="nav-profile"
                        className={`sp-header__profile${current === '/profile' || current.startsWith('/profile/') ? ' sp-header__profile--active' : ''}`}
                        title={t('profile')}
                        aria-label={t('profile')}
                    >
                        {avatarLetter}
                    </a>
                </div>
            </header>

            {sideOpen && (
                <div className="sp-sidebar-overlay" onClick={closeSidebar} role="presentation" />
            )}

            <aside className={`sp-sidebar${sideOpen ? ' sp-sidebar--open' : ''}`}>
                <div className="sp-sidebar__head">
                    <a href="/profile" className="sp-sidebar__user" onClick={closeSidebar}>
                        <div className="sp-sidebar__avatar">{avatarLetter}</div>
                        <div style={{ minWidth: 0 }}>
                            <div className="sp-sidebar__user-name">{username}</div>
                            <div className="sp-sidebar__user-role">{roleBadgeLabel(role, t)} · {t('profile')} →</div>
                        </div>
                    </a>
                    <button
                        type="button"
                        className="sp-sidebar__close"
                        onClick={closeSidebar}
                        aria-label={t('closeMenu')}
                    >
                        ✕
                    </button>
                </div>

                <nav className="sp-sidebar__nav">
                    {menu.map((entry) => {
                        if (entry.type === 'link') {
                            const topLinks = menu
                                .filter((e) => e.type === 'link')
                                .map((e) => ({ href: e.href }));
                            const active = resolveActiveHref(topLinks, current) === entry.href;
                            return (
                                <a
                                    key={entry.href}
                                    href={entry.href}
                                    data-onboarding={ONBOARDING_ATTR[entry.href] || undefined}
                                    className={`sp-sidebar__link${active ? ' sp-sidebar__link--active' : ''}`}
                                    onClick={closeSidebar}
                                >
                                    <span className="sp-sidebar__link-icon">{entry.icon}</span>
                                    <span>{t(entry.labelKey)}</span>
                                </a>
                            );
                        }
                        return (
                            <NavGroup
                                key={entry.id}
                                group={entry}
                                pathname={current}
                                expanded={expandedGroupId === entry.id}
                                onToggle={() => toggleGroup(entry.id)}
                                onNavigate={closeSidebar}
                                t={t}
                                badges={badges}
                            />
                        );
                    })}
                </nav>

                <div className="sp-sidebar__foot">
                    <LanguageSwitcher className="sp-sidebar__locale" variant="select" />
                    <Button variant="outline" type="button" onClick={logout} style={{ width: '100%', marginTop: 10 }}>
                        {t('logout')}
                    </Button>
                </div>
            </aside>
        </>
    );
}
